import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getEmployeesById,
  createEmployee,
  editEmployee,
  getAllBloggers,
  getAllEmployees,
  updateManagedEmployees,
} from "../utils/methods";
import { UploadImage, InputComp } from "../components";
import { strings } from "../utils/strings";
import Button from "../components/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import useFetchOptions from "../hooks/useFetchOptions";
import { toast } from "react-toastify";
import emailjs from "emailjs-com";

const EmployeeEdit = () => {
  const { id: currentId } = useParams();
  const navigate = useNavigate();
  const [employeeData, setEmployeeData] = useState({});
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [emergencyPhoneNo, setEmergencyPhoneNo] = useState("");
  const [cnic, setCnic] = useState("");
  const [designation, setDesignation] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [resignationDate, setResignationDate] = useState("");
  const [employeeImageUrl, setEmployeeImageUrl] = useState("");
  const [employeeImageFile, setEmployeeImageFile] = useState(null);
  const [degreeImageUrl, setDegreeImageUrl] = useState("");
  const [degreeImageFile, setDegreeImageFile] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [lineManager, setLineManager] = useState("");
  const [data, setData] = useState("");

  const { employeeDesignation, loading, error, managers } = useFetchOptions();

  const generateRandomCode = () => {
    const randomCode = Math.floor(10000000 + Math.random() * 90000000);
    return randomCode.toString();
  };

  useEffect(() => {
    const loadEmployeeData = async () => {
      if (currentId !== "new") {
        try {
          const employee = await getEmployeesById(currentId);
          setEmployeeData(employee);
          setFirstName(employee.firstName || "New Employee");
          setEmail(employee.email || "");
          setPhoneNo(employee.phoneNo || "");
          setEmergencyPhoneNo(employee.emergencyPhoneNo || "");
          setResignationDate(employee.resignationDate || "");
          setCnic(employee.cnic || "");
          setDesignation(employee.designation || "");
          setJoiningDate(employee.joiningDate || "");
          setEmployeeImageUrl(employee.employeeImageUrl || "");
          setDegreeImageUrl(employee.degreeImageUrl || "");
          setLineManager(employee.lineManager || "");
        } catch (error) {
          console.error("Error loading Employees:", error);
        }
      }
    };
    loadEmployeeData();
  }, [currentId]);

  const handleTitleChange = (key, newTitle) => {
    setFirstName(newTitle);
    setEmployeeData((prevData) => ({ ...prevData, [key]: newTitle }));
  };

  const handleInputChange = (key, value) => {
    setEmployeeData((prevData) => ({ ...prevData, [key]: value }));
    if (key === "phoneNo") setPhoneNo(value);
    if (key === "emergencyPhoneNo") setEmergencyPhoneNo(value);
    if (key === "cnic") setCnic(value);
    if (key === "joiningDate") setJoiningDate(value);
    if (key === "resignationDate") setResignationDate(value || "");
  };

  const handleEmailChange = (key, value) => {
    setEmployeeData((prevData) => ({ ...prevData, [key]: value }));
  };

  const handleOptionChange = (field, newValue) => {
    if (field === "designation") {
      setDesignation(newValue);
    } else if (field === "lineManager") {
      setLineManager(newValue || "");
    }

    setEmployeeData((prevData) => ({
      ...prevData,
      [field]: newValue || "",
    }));
  };

  const handleEmployeeImageUpload = (url, file) => {
    setEmployeeImageUrl(url);
    setEmployeeImageFile(file);
    setEmployeeData((prevData) => ({ ...prevData, image: url }));
  };

  const handleDegreeImageUpload = (url, file) => {
    setDegreeImageUrl(url);
    setDegreeImageFile(file);
    setEmployeeData((prevData) => ({ ...prevData, degreeImage: url }));
  };

  useEffect(() => {
    const data = async () => {
      setData(managers);
    };
    data();
  }, []);

  const handleSave = async () => {
    const fieldsToValidate = [
      {
        key: "firstName",
        value: firstName,
        label: strings["employeeName"],
        regex: null,
      },
      {
        key: "email",
        value: employeeData.email,
        label: strings["employeeEmail"],
        regex: /^[^\s@]+@exeonic\.com$/,
      },
      {
        key: "cnic",
        value: cnic,
        label: strings["employeeCnic"],
        regex: /^\d{5}-\d{7}-\d{1}$/,
      },
      {
        key: "phoneNo",
        value: phoneNo,
        label: strings["employeePhone"],
        regex: /^(\+92|0)?3\d{9}$/,
      },
      {
        key: "emergencyPhoneNo",
        value: emergencyPhoneNo,
        label: strings["employeeEmgPhone"],
        regex: /^(\+92|0)?3\d{9}$/,
      },
      {
        key: "designation",
        value: designation,
        label: strings["employeeDesignation"],
        regex: null,
      },
      {
        key: "lineManager",
        value: lineManager?.data || "",
        label: strings["lineManager"],
        regex: null,
        optional: true,  // Mark as optional
      },
      {
        key: "joiningDate",
        value: joiningDate,
        label: strings["joiningDate"],
        regex: null,
      },
      {
        key: "resignationDate",
        value: resignationDate || "",
        label: strings["employeeRegDate"],
        regex: null,
      },
    ];

    let isFormValid = true;

    fieldsToValidate.forEach(({ key, value, label, regex }) => {
      if (value && regex && !regex.test(value)) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid.`);
      } else if (!value && regex) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid if provided.`);
      }
    });

    if (!isFormValid) return;

    try {
      const newEmployee = {
        firstName,
        email: employeeData.email,
        phoneNo,
        emergencyPhoneNo,
        cnic,
        designation,
        joiningDate,
        employeeImageUrl,
        degreeImageUrl,
        resignationDate,
        lineManager: lineManager?.data || null,
        lineManagerId: lineManager?.id || null,
        isFirstLogin: true,
      };

      if (currentId === "new") {
        setIsDisabled(true);
        const generatedCode = generateRandomCode();
        const newEmployeeWithPassword = {
          ...newEmployee,
          password: generatedCode
        };
        await createEmployee(newEmployeeWithPassword);

        if (employeeData && generatedCode) {
          const sendEmail = () => {
            const templateParams = {
              to_email: employeeData.email,
              random_code: generatedCode,
              site_link: "https://hr.exeonic.com",
            };
            console.log("templateParams", templateParams);
            emailjs
              .send(
                "service_kvpzs4f", // EmailJS service ID
                "template_1enrggl", // EmailJS template ID
                templateParams,
                "2VV1cH6-a0kM9RqRD" // Your emailjs user ID
              )
              .then(
                (result) => {
                  console.log("Email sent successfully: ", result.text);
                },
                (error) => {
                  console.error("Error sending email: ", error.text);
                }
              );
          };

          sendEmail();
        }
      } else {
        setIsDisabled(true);

        await editEmployee(currentId, {
          ...newEmployee,
          employeeImageFile,
          degreeImageFile,
        });

        await updateManagedEmployees(lineManager.data, currentId);
      }

      navigate(`/employee`);
    } catch (error) {
      setIsDisabled(false);
      console.error("Error saving Employee:", error);
      toast.error("Failed to save employee.");
    }
  };

  if (loading) return <p>...</p>;
  if (error) return <p>Error loading options: {error.message}</p>;

  return (
    <div className="bg-border w-full h-full overflow-scroll px-[212px] pt-12 pb-[60px]">
      <p className="text-[36px] font-semibold">
        {currentId === "new"
          ? `${strings["addEmployeeTitle"]} ${employeeData.firstName || "New"}`
          : `${strings["editEmployeeBtn"]} ${employeeData.firstName}`}
      </p>

      {/* Title */}
      <InputComp
        label={strings["employeeName"]}
        placeHolder={strings["placeholderName"]}
        type="text"
        value={firstName}
        handleChange={handleTitleChange}
        inputKey="firstName"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
        required
      />

      {/* Email */}
      <InputComp
        label={strings["employeeEmail"]}
        placeHolder={strings["placeholderEmail"]}
        type="text"
        value={employeeData.email || ""}
        handleChange={handleEmailChange}
        inputKey="email"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
        required
        regex={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
      />

      <InputComp
        label={strings["employeeCnic"]}
        placeHolder={strings["placeholderCnic"]}
        type="text"
        value={cnic}
        handleChange={handleInputChange}
        inputKey="cnic"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
        required
        regex={/^\d{5}-\d{7}-\d{1}$/}
      />

      <InputComp
        label={strings["employeePhone"]}
        placeHolder={strings["placeholderPhone"]}
        type="text"
        value={phoneNo}
        handleChange={handleInputChange}
        inputKey="phoneNo"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
        required
        regex={/^(\+92|0)?3\d{9}$/}
      />

      <InputComp
        label={strings["employeeEmgPhone"]}
        placeHolder={strings["placeholderEmgPhone"]}
        type="text"
        value={emergencyPhoneNo}
        handleChange={handleInputChange}
        inputKey="emergencyPhoneNo"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
        required
      />

      <InputComp
        label={strings["employeeRegDate"]}
        placeHolder={strings["employeeRegDate"]}
        type="date"
        value={resignationDate}
        handleChange={handleInputChange}
        inputKey="resignationDate"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium text-color-1">
            {strings["employeeDesignation"]}
          </label>
        </div>
        <Autocomplete
          className="w-full"
          options={employeeDesignation}
          getOptionLabel={(option) => option || ""}
          value={designation}
          onChange={(event, newValue) =>
            handleOptionChange("designation", newValue)
          }
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
        />
      </div>

      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium text-color-1">
            {strings["lineManager"]}
          </label>
        </div>

        <Autocomplete
          className="w-full"
          options={managers}
          value={lineManager}
          getOptionLabel={(manager) => manager?.data || ""}
          key={managers.length}
          onChange={(event, newValue) =>
            handleOptionChange("lineManager", newValue)
          }
          disableClearable={false}  // Allow clearing the selection
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
        />
      </div>

      <InputComp
        label={strings["joiningDate"]}
        placeHolder={strings["placeholderJoiningDate"]}
        type="date"
        value={joiningDate}
        handleChange={handleInputChange}
        inputKey="joiningDate"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      <div className="mt-[20px]">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1 mt-[20px]">
            {strings["employeeImage"]}
          </label>
        </div>

        {/* Employee Image */}
        <UploadImage
          onImageUpload={handleEmployeeImageUpload}
          blogId={currentId === "new" ? null : currentId}
          imageType="image"
          initialImageUrl={employeeData.image} // Pass the initial image URL
        />
      </div>

      <div className="my-[20px]">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
            {strings["recentDegree"]}
          </label>
        </div>

        <UploadImage
          onImageUpload={handleDegreeImageUpload}
          blogId={currentId === "new" ? null : currentId}
          imageType="image"
          initialImageUrl={employeeData.degreeImage}
        />
      </div>

      {/* Upload Button */}
      <div className="flex justify-center w-full">
        <Button
          classes={`!w-full justify-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          onClick={isDisabled ? null : handleSave}
          disabled={isDisabled}
        >
          {strings["submitButton"]}
        </Button>
      </div>
    </div>
  );
};

export default EmployeeEdit;
